// src/components/groups/FederationApplyModal.jsx
// A group applies to federate with another group (Federation 4B). Two steps: pick which of
// the viewer's eligible groups is applying (skipped when there's exactly one — the group-picker
// is the one place "which group am I acting as" becomes a real question, per the build brief),
// then answer the target's federation_application_schema (4A's output) — or, when that schema
// is null/empty, a plain confirm step rather than a broken or empty form.

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';
import Modal from '../core/Modal';
import { groupService } from '../../data/dataService';
import { addBreadcrumb } from '../../utils/errorCapture';
import { FEDERATION_APPLICATION_TEXT_MAX, FEDERATION_APPLICATION_LONGTEXT_MAX } from '../../utils/validationConstants';

// Same regex already duplicated in LoginRegisterModal.jsx, ForgotPasswordModal.jsx and MyProfile.jsx
// (InviteMembersModal.jsx was deleted and AdminGroup.jsx moved to invitationRules.isPlainEmail in
// Invitations Phase 3, #54) — reused here rather than centralising it (out of this build's scope;
// flagged to the reuse backlog).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FederationApplyModal = ({
  isOpen,
  onClose,
  onSubmit,          // (applicantGroupId, requestData|null) => Promise<{success, ...}>
  federationGroupId,
  federationName,
  applicantGroups = [], // eligible groups for THIS target (already excludes pending ones)
  loading = false
}) => {
  const { t } = useTranslation('groups');
  const theme = useTheme();
  const mauve = theme?.primary || '#BB4D9A';

  const [step, setStep] = useState('pick'); // 'pick' | 'form'
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [schema, setSchema] = useState([]);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});

  // Reset on open; auto-skip the picker when there's exactly one eligible group.
  useEffect(() => {
    if (!isOpen) return;
    const single = applicantGroups.length === 1 ? applicantGroups[0].id : null;
    setSelectedGroupId(single);
    setStep(single ? 'form' : 'pick');
    setAnswers({});
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Read the target's schema fresh on open — never trust a passed-in group prop's shape.
  useEffect(() => {
    if (!isOpen || !federationGroupId) return;
    let cancelled = false;
    setSchemaLoading(true);
    groupService.getFederationApplicationSchema(federationGroupId).then((result) => {
      if (!cancelled) {
        setSchema(result);
        setSchemaLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [isOpen, federationGroupId]);

  if (!isOpen) return null;

  const handlePick = (groupId) => {
    setSelectedGroupId(groupId);
    setStep('form');
  };

  const handleAnswerChange = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  const validate = () => {
    const newErrors = {};
    schema.forEach((field) => {
      const value = (answers[field.key] || '').trim();
      if (field.required && !value) {
        newErrors[field.key] = t('federation_apply_field_required');
      } else if (value && field.type === 'email' && !EMAIL_REGEX.test(value)) {
        newErrors[field.key] = t('federation_apply_field_invalid_email');
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    addBreadcrumb('action', { action: 'federation-apply', component: 'FederationApplyModal' });
    if (!selectedGroupId) return;
    if (schema.length > 0 && !validate()) return;
    await onSubmit(selectedGroupId, schema.length > 0 ? answers : null);
  };

  // One input per schema entry, in stored order, typed by `type`. Be tolerant of a `type` the
  // renderer doesn't recognise — falls back to short text rather than throwing (4B §2: a
  // Federator must never be able to break the applicant's form).
  const renderField = (field) => {
    const value = answers[field.key] || '';
    const commonStyle = {
      width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px',
      fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit'
    };
    if (field.type === 'longtext') {
      return (
        <textarea
          value={value}
          onChange={(e) => handleAnswerChange(field.key, e.target.value)}
          maxLength={FEDERATION_APPLICATION_LONGTEXT_MAX}
          rows={4}
          style={commonStyle}
        />
      );
    }
    const inputType = field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text';
    return (
      <input
        type={inputType}
        value={value}
        onChange={(e) => handleAnswerChange(field.key, e.target.value)}
        maxLength={FEDERATION_APPLICATION_TEXT_MAX}
        style={commonStyle}
      />
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={!loading}
      closeOnBackdrop={() => !loading}
      panelStyle={{
        background: 'white',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '480px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        background: theme?.primary || '#BB4D9A'
      }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'white' }}>
          {t('federation_apply_title')}
        </h2>
        <button
          onClick={onClose}
          disabled={loading}
          style={{
            background: 'transparent', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
            padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: loading ? 0.5 : 1
          }}
        >
          <X size={24} color="white" />
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '20px', maxHeight: 'calc(90vh - 64px)', overflowY: 'auto', boxSizing: 'border-box' }}>
        {step === 'pick' ? (
          <>
            <p style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#374151', lineHeight: '1.5' }}>
              {t('federation_apply_pick_group', { federation: federationName || '' })}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {applicantGroups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => handlePick(g.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', padding: '12px',
                    background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
                    cursor: 'pointer', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151'
                  }}
                >
                  {g.name}
                </button>
              ))}
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            {applicantGroups.length > 1 && (
              <button
                type="button"
                onClick={() => setStep('pick')}
                style={{
                  background: 'none', border: 'none', color: mauve, fontSize: '13px',
                  fontWeight: '600', cursor: 'pointer', padding: 0, marginBottom: '12px'
                }}
              >
                {t('federation_apply_change_group')}
              </button>
            )}

            {schemaLoading ? (
              <p style={{ fontSize: '14px', color: '#6b7280' }}>{t('loading')}</p>
            ) : schema.length === 0 ? (
              <p style={{ margin: '0 0 20px 0', fontSize: '15px', color: '#374151', lineHeight: '1.5' }}>
                {t('federation_apply_confirm_no_requirements', { federation: federationName || '' })}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
                {schema.map((field) => (
                  <div key={field.key}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      {field.label}
                      {field.required && <span style={{ color: '#ef4444' }}> *</span>}
                    </label>
                    {renderField(field)}
                    {errors[field.key] && (
                      <p style={{ color: '#ef4444', fontSize: '12px', margin: '4px 0 0 0' }}>{errors[field.key]}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                style={{
                  padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: 'none',
                  borderRadius: '25px', fontSize: '14px', fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1
                }}
              >
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || schemaLoading}
                style={{
                  padding: '10px 24px', background: theme?.primary || '#BB4D9A', color: 'white',
                  border: 'none', borderRadius: '25px', fontSize: '14px', fontWeight: '600',
                  cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? t('sending_button') : t('federation_apply_submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default FederationApplyModal;
